import React, { useContext, useEffect, useRef, useState } from "react";
import { Grid, useMediaQuery, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useFormContext } from "../Context/Context";
import { useReadOnly } from "../Context/ReadOnlyContext";
import { FinalValuesContext } from "../Context/FinalValuesContext";
import HeadingInput from "./LongText/HeadingInput";
import TextAreaWithCounter from "./LongText/TextAreaWithCounter";
import {
  getInitialValues,
  updateDrawerState,
  clamp400,
} from "./LongText/longTextHelpers";
import ConvertTitle from "./ConvertTitle";

const LongText = ({ id, header }) => {
  /* ---------- Context ---------- */
  const { drawerData } = useFormContext();
  const { readOnly } = useReadOnly();
  const { finalSelectValues } = useContext(FinalValuesContext);
  const location = useLocation();
  const currentPage = location.pathname;
  const idx = drawerData.findIndex((c) => c.id === id);

  /* ---------- Initial Values ---------- */
  const init = getInitialValues(drawerData, idx);
  const [longTextValue, setLongTextValue] = useState(
    header || init.longTextValue
  );
  const [mainTextareaValue, setMainTextareaValue] = useState(
    init.mainTextareaValue
  );
  const [subLongTextValue, setSubLongTextValue] = useState(
    init.subLongTextValue
  );

  /* ---------- UI State ---------- */
  const [subVisible, setSubVisible] = useState(false);
  const [focused, setFocused] = useState(false);
  const [condition, setCondition] = useState(false); // فیلد خواندنی؟

  /* ---------- Derived values ---------- */
  const theme = useTheme();
  const HeadingColor = theme.palette.blue[700];
  const isUpXxl = useMediaQuery((t) => t.breakpoints.up("xxs"));
  const subFont = isUpXxl ? "14px" : "12px";

  /* ---------- Read-only logic ---------- */
  useEffect(() => {
    const ti = finalSelectValues.findIndex((i) => i.name === "FieldCondition");
    const title = ConvertTitle({ title: finalSelectValues[ti]?.value });
    const dIdx = drawerData.findIndex((d) => d.title === title);
    const dataCond = drawerData[dIdx]?.state?.readOnly;
    setCondition(
      currentPage === "/publish" &&
        (readOnly || dataCond) &&
        title === "LongText"
    );
  }, [finalSelectValues, readOnly]);

  /* ---------- Sync with drawer ---------- */
  useEffect(() => {
    updateDrawerState(
      drawerData,
      idx,
      {
        longTextValue,
        mainTextareaValue,
        subLongTextValue,
      },
      readOnly
    );
  }, [longTextValue, mainTextareaValue, subLongTextValue]);

  /* ---------- Click-outside ---------- */
  const containerRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSubVisible(false);
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ---------- Focus handlers ---------- */
  const handleFocus = () => setFocused(true);
  const handleBlur = (e) => {
    if (e.currentTarget && !e.currentTarget.contains(e.relatedTarget)) {
      setFocused(false);
    }
  };

  /* ---------- Global read-only ---------- */
  const isReadOnlyGlobal =
    currentPage.startsWith("/publish") ||
    currentPage.startsWith("/card-form/publish") ||
    currentPage.startsWith("/user/fill-form/");

  return (
    <Grid
      ref={containerRef}
      bgcolor="white"
      p="12px"
      mb="28px"
      borderRadius="3px"
      border={
        focused && !isReadOnlyGlobal
          ? "2px solid #0293e2"
          : "2px solid transparent"
      }
      onClick={() => setSubVisible(true)}
      onFocus={handleFocus}
      onBlur={handleBlur}     
      tabIndex={-1}           
    >
      {/* Heading */}
      <HeadingInput
        value={longTextValue}
        onChange={(e) => setLongTextValue(e.target.value)}
        readOnly={isReadOnlyGlobal || condition}
        color={HeadingColor}
      />

      {/* TextArea */}
      <TextAreaWithCounter
        value={mainTextareaValue}
        onChange={(e) => setMainTextareaValue(clamp400(e.target.value))}
        readOnly={condition}
        color={HeadingColor}
      />

      {/* Sub-heading */}
      {subVisible && !isReadOnlyGlobal && (
        <input
          placeholder="یک عنوان فرعی وارد کنید"
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            fontSize: subFont,
            fontWeight: 500,
            lineHeight: 1.6,
            color: "#57647e",
          }}
          value={subLongTextValue}
          onChange={(e) => setSubLongTextValue(e.target.value)}
          readOnly={isReadOnlyGlobal || condition}
        />
      )}
    </Grid>
  );
};

export default LongText;